<template>
  <form
    @submit.prevent="submitForm"
    class="flex flex-row flex-wrap px-4 mt-4 mb-4"
  >
    <h1 class="w-full text-lg font-medium">Bio Data</h1>
    <div
      class="w-full flex flex-row flex-wrap mt-4 border p-8 rounded-xl shadow-lg"
    >
      <div class="w-1/2 pr-4" :class="{ invalid: !name.isValid }">
        <label for="name">Name</label>
        <input
          type="text"
          id="name"
          v-model.trim="name.val"
          @blur="clearValidity('name')"
        />
        <p v-if="!name.isValid" class="text-xs text-red-600">
          Name must not be empty.
        </p>
      </div>
      <div class="w-1/2 pl-4" :class="{ invalid: !username.isValid }">
        <label for="username">Username</label>
        <div
          v-if="mode == 'update'"
          class="border rounded-lg px-4 py-2 bg-gray-100"
        >
          {{ username.val }}
        </div>
        <input
          v-else
          type="text"
          id="username"
          v-model.trim="username.val"
          @blur="clearValidity('username')"
        />
        <p v-if="!username.isValid" class="text-xs text-red-600">
          Username must not be empty.
        </p>
      </div>
      <div class="mt-4 w-1/2 pr-4" :class="{ invalid: !email.isValid }">
        <label for="email">Email (Optional)</label>
        <input
          type="email"
          id="email"
          v-model.trim="email.val"
          @blur="clearValidity('email')"
        />
        <p v-if="!email.isValid" class="text-xs text-red-600">
          {{ email.msg }}
        </p>
      </div>
      <div class="mt-4 w-1/2 pl-4" :class="{ invalid: !phone.isValid }">
        <label for="phone">Phone (Optional)</label>
        <input
          type="text"
          id="phone"
          v-model.trim="phone.val"
          maxlength="10"
          @blur="clearValidity('phone')"
          @keypress="isNumber($event)"
        />
        <p v-if="!phone.isValid" class="text-xs text-red-600">
          {{ phone.msg }}
        </p>
      </div>
      <div class="mt-4 w-1/2 pr-4" :class="{ invalid: !password.isValid }">
        <label for="password">Password</label>
        <input
          type="text"
          id="password"
          v-model.trim="password.val"
          @blur="clearValidity('password')"
        />
        <p v-if="!password.isValid" class="text-xs text-red-600">
          Password must not be empty.
        </p>
      </div>
    </div>

    <h1 class="w-full text-lg font-medium mt-8">Payment Data</h1>
    <div
      class="w-full grid grid-cols-2 gap-8 mt-4 border p-8 rounded-xl shadow-lg"
    >
      <!-- Banks only support UPI payment method -->
      <div class="w-full">
        <label for="paymentMethod">Payment Method</label>
        <div class="border rounded-lg px-4 py-2 bg-gray-100">
          UPI
        </div>
      </div>

      <div class="w-full" :class="{ invalid: !upiId.isValid }">
        <label for="upi-id">UPI ID / QR Code</label>
        <div v-if="mode == 'update' && upiId.val" class="border rounded-lg px-4 py-2 bg-gray-100 mb-2">
          {{ upiId.val }}
        </div>
        <input
          v-if="mode == 'create'"
          type="text"
          id="upi-id"
          v-model.trim="upiId.val"
          placeholder="e.g., user@bank or upload QR"
          @blur="clearValidity('upiId')"
        />
        <p v-if="!upiId.isValid" class="text-xs text-red-600">
          UPI Id is required.
        </p>

        <!-- QR Code Upload for UPI -->
        <div class="mt-4">
          <div class="flex-shrink-0">
              <div v-if="qrPreview" class="relative">
                  <div @click.stop="cancelUploadedPhoto" class="absolute top-0 left-0 cursor-pointer h-6 w-6 border border-red-600 rounded-full flex flex-col items-center justify-center">
                      <icon-close class="h-4 w-4 text-red-600"></icon-close>
                  </div>
                  <img class="h-48 object-contain" :src="qrPreview" >
              </div>
              <img v-else class="h-48" src="../../assets/images/default/thumbnail.jpeg" >
              <div class="flex justify-start mt-4">
                  <input ref="qrPicture" class="hidden" id="qr-picture" @change="handleUploadedQR" type="file" accept="image/*">
                  <div @click.stop="onPickFile('qrPicture')" class="flex items-center cursor-pointer text-blue-600 hover:text-blue-700">
                      <icon-plus class="h-4 w-4"></icon-plus>
                      <span class="ml-1">Upload QR Code<span class="text-sm text-gray-500 ml-1">(JPG, JPEG, PNG max 2MB)</span></span>
                  </div>
              </div>
              <transition name="fade" mode="out-in">
                  <p v-if="!qrPicture.isValid" class="h-4 text-xs text-red-600">
                      {{ qrPicture.msg }}
                  </p>
              </transition>
          </div>
        </div>
      </div>
    </div>

    <h1 class="w-full text-lg font-medium mt-8">Restrictions & Limits</h1>
    <div
      class="w-full grid grid-cols-2 gap-8 mt-4 border p-8 rounded-xl shadow-lg"
    >
      <div class="w-full" :class="{ invalid: !minOrderAmount.isValid }">
        <label for="min-order-amount">Min Order Amount</label>
        <input
          type="number"
          id="min-order-amount"
          v-model.trim="minOrderAmount.val"
          min="0"
          step="0.01"
          @blur="clearValidity('minOrderAmount')"
        />
        <p v-if="!minOrderAmount.isValid" class="text-xs text-red-600">
          {{ minOrderAmount.msg }}
        </p>
      </div>
      <div class="w-full" :class="{ invalid: !maxOrderAmount.isValid }">
        <label for="max-order-amount">Max Order Amount</label>
        <input
          type="number"
          id="max-order-amount"
          v-model.trim="maxOrderAmount.val"
          min="0"
          step="0.01"
          @blur="clearValidity('maxOrderAmount')"
        />
        <p v-if="!maxOrderAmount.isValid" class="text-xs text-red-600">
          {{ maxOrderAmount.msg }}
        </p>
      </div>
      <div class="w-full" :class="{ invalid: !maxOrders.isValid }">
        <label for="max-orders">Max Orders (Lifetime)</label>
        <input
          type="number"
          id="max-orders"
          v-model.trim="maxOrders.val"
          min="0"
          @blur="clearValidity('maxOrders')"
        />
        <p v-if="!maxOrders.isValid" class="text-xs text-red-600">
          {{ maxOrders.msg }}
        </p>
      </div>
      <div class="w-full" :class="{ invalid: !maxBalance.isValid }">
        <label for="max-balance">Max Balance</label>
        <input
          type="number"
          id="max-balance"
          v-model.trim="maxBalance.val"
          min="0"
          step="0.01"
          @blur="clearValidity('maxBalance')"
        />
        <p v-if="!maxBalance.isValid" class="text-xs text-red-600">
          {{ maxBalance.msg }}
        </p>
      </div>
    </div>

    <h1 class="w-full text-lg font-medium mt-8">Operations Data</h1>
    <div
      class="w-full grid grid-cols-2 gap-8 mt-4 border p-8 rounded-xl shadow-lg"
    >
      <div class="w-full" :class="{ invalid: !openingBalance.isValid }">
        <label for="opening-balance">Opening Balance</label>
        <div v-if="mode == 'update'" class="border rounded-lg px-4 py-2 bg-gray-100">
          {{ openingBalance.val }} (Read-only)
        </div>
        <input
          v-else
          type="number"
          id="opening-balance"
          v-model.trim="openingBalance.val"
          min="0"
          @blur="clearValidity('openingBalance')"
        />
        <p v-if="!openingBalance.isValid" class="text-xs text-red-600">
          Opening Balance must not be empty.
        </p>
      </div>

      <div class="w-full" :class="{ invalid: !payInLimit.isValid }">
        <label for="pay-in-limit">Pay In Limit</label>
        <input
          type="number"
          id="pay-in-limit"
          v-model.trim="payInLimit.val"
          min="0"
          @blur="clearValidity('payInLimit')"
        />
        <p v-if="!payInLimit.isValid" class="text-xs text-red-600">
          Pay In Limit must not be empty.
        </p>
      </div>
      <div class="w-full" :class="{ invalid: !payOutLimit.isValid }">
        <label for="pay-out-limit">Pay Out Limit</label>
        <input
          type="number"
          id="pay-out-limit"
          v-model.trim="payOutLimit.val"
          min="0"
          @blur="clearValidity('payOutLimit')"
        />
        <p v-if="!payOutLimit.isValid" class="text-xs text-red-600">
          Pay Out Limit must not be empty.
        </p>
      </div>
      <div class="w-full" :class="{ invalid: !payOut.isValid }">
        <label for="pay-out">Pay Out</label>
        <input type="checkbox" id="pay-out" v-model.trim="payOut.val" />
      </div>
    </div>

    <div class="w-full flex flex-col justify-center items-center mt-8">
      <p v-if="!formIsValid" class="mb-4 text-red-600">
        Please fix the above errors and submit again.
      </p>
      <base-button
        ><span class="capitalize">{{ mode }}</span></base-button
      >
    </div>
  </form>
</template>

<script>
// import http from "../../http-common.js";
import IconClose from "../../components/icons/IconClose.vue";
import IconPlus from "../../components/icons/IconPlus.vue";

export default {
  name: "create-user-form",
  emits: ["create-user"],
  props: {
    mode: String,
    selectedUser: Object,
  },
  components: {
    IconClose,
    IconPlus,
  },
  mounted() {
    if (this.mode == "update") {
      this.setUser();
    }
    // Load Tesseract for QR text extraction
    if (!window.Tesseract) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
      script.async = true;
      document.head.appendChild(script);
    }
  },
  data() {
    return {
      name: {
        val: "",
        isValid: true,
        msg: "",
      },
      username: {
        val: "",
        isValid: true,
        msg: "",
      },
      email: {
        val: "",
        isValid: true,
        msg: "",
      },
      phone: {
        val: "",
        isValid: true,
        msg: "",
      },
      password: {
        val: "",
        isValid: true,
        msg: "",
      },
      upiId: {
        val: "",
        isValid: true,
        msg: "",
      },
      openingBalance: {
        val: 0,
        isValid: true,
        msg: "",
      },
      payInLimit: {
        val: 0,
        isValid: true,
        msg: "",
      },
      payOutLimit: {
        val: 0,
        isValid: true,
        msg: "",
      },
      payOut: {
        val: false,
        isValid: true,
        msg: "",
      },
      paymentMethod: {
        val: "UPI",
        isValid: true,
        msg: "",
      },
      minOrderAmount: {
        val: null,
        isValid: true,
        msg: "",
      },
      maxOrderAmount: {
        val: null,
        isValid: true,
        msg: "",
      },
      maxOrders: {
        val: null,
        isValid: true,
        msg: "",
      },
      maxBalance: {
        val: null,
        isValid: true,
        msg: "",
      },
      qrPicture: {
          val: null,
          isValid: true,
          msg: "",
      },
      qrPreview: null,
      formIsValid: true,
    };
  },
  methods: {
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    extractUPIFromText(text) {
      // Normalize whitespace
      const normalized = text.replace(/\s+/g, ' ').toLowerCase();

      // Look for common UPI ID patterns: user@bank or similar
      const upiMatch = normalized.match(/[a-z0-9._-]+@[a-z0-9._-]+/i);
      if (upiMatch) {
        return upiMatch[0];
      }

      // If no match, return empty
      return null;
    },
    async extractUPIFromQR() {
      if (!this.qrPicture.val) return;

      try {
        // Wait for Tesseract to load
        if (!window.Tesseract) {
          console.error('Tesseract.js not loaded yet');
          return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const image = event.target.result;
            const { data: { text } } = await window.Tesseract.recognize(image, 'eng');
            const upi = this.extractUPIFromText(text);
            if (upi) {
              this.upiId.val = upi;
            }
          } catch (error) {
            console.error('Error during Tesseract recognition:', error);
          }
        };
        reader.readAsDataURL(this.qrPicture.val);
      } catch (error) {
        console.error('Error extracting UPI from QR:', error);
      }
    },
    validateForm() {
      this.formIsValid = true;

      if (this.mode == "create") {
        if (this.username.val === "") {
          this.username.isValid = false;
          this.formIsValid = false;
        }
        if (this.password.val === "") {
          this.password.isValid = false;
          this.formIsValid = false;
        }
      }

      if (this.name.val === "") {
        this.name.isValid = false;
        this.formIsValid = false;
      }

      if (this.upiId.val === "") {
        this.upiId.isValid = false;
        this.formIsValid = false;
      }

      if (this.payInLimit.val === "") {
        this.payInLimit.isValid = false;
        this.formIsValid = false;
      }
      if (this.payOutLimit.val === "") {
        this.payOutLimit.isValid = false;
        this.formIsValid = false;
      }
    },
    submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      let formData = {
        username: this.username.val,
        name: this.name.val,
        email: this.email.val,
        phone: this.phone.val,
        upiid: this.upiId.val,
        payIn: 1, // Default true and hidden
        payOut: this.payOut.val ? 1 : 0,
        payInLimit: this.payInLimit.val,
        payOutLimit: this.payOutLimit.val,
        balance: this.openingBalance.val,
        paymentMethod: "UPI", // Only UPI for banks
        is_utr_enabled: 1, // Hide and default to Yes
        isPayNow: 1, // Hide and enable by default
        website: null, // Hide this field
        minOrderAmount: this.minOrderAmount.val,
        maxOrderAmount: this.maxOrderAmount.val,
        maxOrders: this.maxOrders.val,
        maxBalance: this.maxBalance.val,
        qr_picture: this.qrPicture.val,
        qr: this.mode == 'create' ? '' : this.selectedUser.qr,
        qrPreview: this.qrPreview,
      };

      if (this.mode == "create") {
        formData.password = this.password.val;
      } else {
        if (this.password.val != "") {
          formData.password = this.password.val;
        }
      }

      this.$emit("create-user", formData);
    },
    isNumber: function(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    setUser() {
      this.name.val = this.selectedUser.name;
      this.username.val = this.selectedUser.username;
      this.email.val = this.selectedUser.email;
      this.phone.val = this.selectedUser.phone;
      this.upiId.val = this.selectedUser.upiId;
      this.openingBalance.val = this.selectedUser.balance;
      this.payOut.val = this.selectedUser.payOut ? true : false;
      this.payInLimit.val = this.selectedUser.payInLimit;
      this.payOutLimit.val = this.selectedUser.payOutLimit;
      this.minOrderAmount.val = this.selectedUser.minOrderAmount || null;
      this.maxOrderAmount.val = this.selectedUser.maxOrderAmount || null;
      this.maxOrders.val = this.selectedUser.maxOrders || null;
      this.maxBalance.val = this.selectedUser.maxBalance || null;
      if (this.selectedUser.qr) {
          this.qrPreview = this.selectedUser.qr;
      }
    },
    cancelUploadedPhoto() {
      this.qrPicture.val = null;
      this.qrPreview = null;
    },
    onPickFile(ref) {
      this.$refs[ref].click();
    },
    handleUploadedQR(event) {
        this.qrPicture.isValid = true;
        this.qrPicture.msg = "";
        const file = event.target.files[0];
        if (file) {
            if (!file.type.includes('image/')) {
                this.qrPicture.isValid = false;
                this.qrPicture.msg = 'Only JPG, JPEG and PNG files are allowed.';
            } else if (file.size > 2 * 1024 * 1024) {
                this.qrPicture.isValid = false;
                this.qrPicture.msg = 'File size must be less than 2MB.';
            } else {
                this.qrPicture.isValid = true;
                this.qrPicture.msg = '';
                this.qrPicture.val = file;
                this.qrPreview = URL.createObjectURL(file);
                // Extract UPI from QR
                this.extractUPIFromQR();
            }
        }
    },
  },
};
</script>
